import React from 'react'
import { Utils } from '@bsv/sdk'
import api from 'api/api'
import type { OrdinalWalletActionResult } from 'api/api'
import { formatUsdValue, satsToUsd } from 'api/useBsvExchangeRate'
import { accessTokens } from 'api/accessTokenMgmt'
import { getClientShare } from 'lib/bsv-signing'
import { currentUserUsesClientSigning } from 'lib/clientSigningFeature'
import useStore from 'state/knovStore'
import { subscribePaymentUpdate } from 'state/channels/useUserChannel'
import { inputSatoshis, outputsToAddress } from './actionAmounts'
import { ActionSummary, actionSummary } from './actionSummary'
import {
    ActionOutput,
    BridgeError,
    BridgeInvocation,
    CWI_MESSAGE_TYPE,
    ORDINAL_WALLET_VERSION,
    ValidatedCreateAction,
    WALLET_ERROR,
    describeLockingScript,
    errorResponse,
    hexToBytes,
    parseInvocation,
    successResponse,
    validateAbortActionArgs,
    validateCreateActionArgs,
    validateSignActionArgs,
} from './protocol'

export interface OrdinalBridgeActor {
    username: string | null
    address: string | null
}

// The TREECHAT calls this host answers. An ordinal reads them from getContext to tell what
// the Treechat it runs in supports, instead of guessing from a version number.
export const ORDINAL_CONTEXT_VERSION = 2
export const TREECHAT_CAPABILITIES = [
    'getContext',
    'getViewer',
    'getUsers',
    'openProfile',
    'openPost',
] as const

// What an ordinal learns about where it is running (TREECHAT getContext), besides the
// version and capabilities the host adds.
export interface OrdinalContextPayload {
    ordinal: { origin: string }
    creator: OrdinalBridgeActor | null
    owner: OrdinalBridgeActor | null
    // null until the viewer connects this ordinal
    viewerIsOwner: boolean | null
    post: { answerId: string; threadId: string | null } | null
    theme: 'light' | 'dark'
}

export interface OrdinalBridgeFrame {
    origin: string
    label: () => string
    context: () => OrdinalContextPayload
    /** Returns the ordinal to its static preview, when another ordinal starts. */
    stop?: () => void
    /** The Treechat user ID behind a username the context names (creator or owner). */
    contextUserId?: (username: string) => string | null
    /** Opens a profile in the panel to the right. */
    openProfile?: (userId: string) => void
    /** Opens the post the ordinal is in, when it has one. */
    openPost?: () => void
}

export interface OrdinalBridgeUser {
    username: string | null
    avatarUrl: string | null
}

// The ordinal is identified by the iframe it posts from. Every HTML ordinal loads from
// the same content host, so the message origin can't tell two ordinals apart. Frames are
// keyed by element and matched to event.source when a message arrives: a lazy iframe has
// no contentWindow (WebKit) until it starts loading.
const frames = new Map<HTMLIFrameElement, OrdinalBridgeFrame>()
const connections = new Map<string, 'granted' | 'denied'>()
const pendingConnections = new Map<string, Promise<boolean>>()
const pendingActions = new Set<string>()
// Cancel declines one payment, and the ordinal can't ask again for a few seconds, so it can't
// reopen the tray in a loop. "Block this ordinal" refuses its payments for the rest of the
// visit. Both are keyed by the ordinal's origin outpoint.
const DECLINE_COOLDOWN_MS = 5000
const declinedAt = new Map<string, number>()
const blockedPayments = new Set<string>()
// Ordinals with a payment whose start couldn't be confirmed: no new payment until reload.
const unsettledOrigins = new Set<string>()
// Deferred actions waiting for the ordinal's signAction, by BRC-100 reference.
const deferredActions = new Map<
    string,
    { paymentId: string; origin: string; returnTXIDOnly: boolean; expiresAt: number }
>()
// The server gives up on an action the ordinal never unlocks after 10 minutes.
const DEFERRED_ACTION_TTL_MS = 11 * 60 * 1000

/** Forget deferred actions the server has already given up on. */
function pruneDeferredActions() {
    for (const [reference, entry] of deferredActions) {
        if (entry.expiresAt < Date.now()) deferredActions.delete(reference)
    }
}
// getUsers: names behind payment addresses. Each answer is kept for the visit, an ordinal
// asks for at most 50 addresses at a time and 500 in all, and the server limits the viewer.
const MAX_USERS_PER_CALL = 50
const MAX_USERS_PER_ORDINAL = 500
const PAYMENT_ADDRESS = /^1[1-9A-HJ-NP-Za-km-z]{25,34}$/
const usersByAddress = new Map<string, { user: OrdinalBridgeUser; id: string } | null>()
const addressesAskedBy = new Map<string, Set<string>>()
// Usernames an ordinal has been told, with their user IDs: openProfile opens only these.
const profilesShownTo = new Map<string, Map<string, string>>()
// An ordinal may open one panel every couple of seconds, so it can't flood the page.
const APP_ACTION_GAP_MS = 2000
const lastAppActionAt = new Map<string, number>()
let promptQueue: Promise<unknown> = Promise.resolve()
let listening = false

const POLL_INTERVAL_MS = 1500
const START_ATTEMPTS = 3
const START_RETRY_MS = 2000
const SLOW_POLL_INTERVAL_MS = 5000
// PaymentWorkflow may wait up to 45 minutes for funds, then retry its broadcast. Reporting
// failure sooner invites the ordinal to retry and pay twice.
const ACTION_TIMEOUT_MS = 70 * 60 * 1000
const BEEF_GRACE_MS = 30 * 1000
// How long the wallet waits to lay out a deferred action before giving up.
const UNLOCK_READY_TIMEOUT_MS = 10 * 60 * 1000
// A deferred action is funded while the confirm tray is open. The server gives up on one the
// ordinal hasn't unlocked within 10 minutes, so an older one is dropped and funded again.
const PREPARED_MAX_AGE_MS = 8 * 60 * 1000
const DISCARD_ATTEMPTS = 3
const RELEASE_CHECKS = 20

// One ordinal holds the wallet at a time. Ordinals share a content host, so one running beside
// another can script its frame and use its connection. A starting ordinal registers before its
// frame gets a src (useOrdinalWalletBridge), and registering stops every other one: its iframe
// leaves the document, which ends its page at once, and its connection is dropped.
function stopFrame(iframe: HTMLIFrameElement, frame: OrdinalBridgeFrame) {
    frames.delete(iframe)
    connections.delete(frame.origin)
    iframe.remove()
    frame.stop?.()
}

export function registerOrdinalFrame(
    iframe: HTMLIFrameElement,
    frame: OrdinalBridgeFrame,
): () => void {
    for (const [other, running] of [...frames]) {
        if (other !== iframe) stopFrame(other, running)
    }
    frames.set(iframe, frame)
    if (!listening) {
        window.addEventListener('message', onMessage)
        listening = true
    }
    return () => {
        if (frames.get(iframe) === frame) frames.delete(iframe)
    }
}

// The frame's window, while it still shows the content it was given: an ordinal that
// navigates its frame elsewhere doesn't hand the bridge to that page.
function frameFor(event: MessageEvent): OrdinalBridgeFrame | undefined {
    if (!event.source) return undefined
    for (const [iframe, frame] of frames) {
        if (iframe.contentWindow !== event.source) continue
        // A second load of the same src means the page inside navigated, maybe to another
        // ordinal on the same content host: that page is not the one the viewer sees named.
        if (Number(iframe.dataset?.bridgeLoads || 0) > 1) return undefined
        const expected = new URL(iframe.src, window.location.href).origin
        return event.origin === expected ? frame : undefined
    }
    return undefined
}

function onMessage(event: MessageEvent) {
    if (!event.isTrusted) return
    const source = event.source as Window | null
    const frame = frameFor(event)
    if (!source || !frame) return
    const invocation = parseInvocation(event.data)
    if (!invocation) return

    const targetOrigin = event.origin && event.origin !== 'null' ? event.origin : '*'
    // A starting ordinal stops the others (registerOrdinalFrame); while one is still being torn
    // down, nobody gets an answer.
    const answer =
        frames.size > 1
            ? Promise.reject(new BridgeError('Another ordinal is starting; try again in a moment'))
            : handleInvocation(invocation, frame)
    answer.then(
        result => source.postMessage(successResponse(invocation, result), targetOrigin),
        error => source.postMessage(errorResponse(invocation, error), targetOrigin),
    )
}

export async function handleInvocation(
    invocation: BridgeInvocation,
    frame: OrdinalBridgeFrame,
): Promise<unknown> {
    if (invocation.type === CWI_MESSAGE_TYPE) {
        switch (invocation.call) {
            case 'getVersion':
                return { version: ORDINAL_WALLET_VERSION }
            case 'getNetwork':
                return { network: 'mainnet' }
            case 'isAuthenticated':
            case 'waitForAuthentication':
                requireSignedIn()
                return { authenticated: true }
            case 'getPublicKey':
                return getPublicKey(invocation.args, frame)
            case 'createAction':
                return createAction(invocation.args, frame)
            case 'signAction':
                return signAction(invocation.args, frame)
            case 'abortAction':
                return abortAction(invocation.args, frame)
            default:
                throw new BridgeError(
                    `${invocation.call} is not supported`,
                    WALLET_ERROR.unsupportedAction,
                )
        }
    }

    switch (invocation.call) {
        case 'getContext': {
            const context = {
                ...frame.context(),
                version: ORDINAL_CONTEXT_VERSION,
                capabilities: [...TREECHAT_CAPABILITIES],
            }
            // Whether the viewer owns this ordinal says who they are; that waits for Connect.
            const connected = connections.get(frame.origin) === 'granted'
            return connected ? context : { ...context, viewerIsOwner: null }
        }
        case 'getViewer': {
            await requireConnection(frame)
            const user = window.gon.currentUser!
            const username = user.name || user.username || null
            if (username) showProfile(frame, username, String(user.id))
            return {
                username,
                avatarUrl: user.user_image_url || null,
                identityKey: await identityKey(),
                paymentAddress: await viewerPaymentAddress(),
            }
        }
        case 'getUsers':
            return getUsers(invocation.args, frame)
        case 'openProfile': {
            const username = (invocation.args as { username?: unknown } | null)?.username
            const userId =
                typeof username === 'string'
                    ? profilesShownTo.get(frame.origin)?.get(username) ??
                      frame.contextUserId?.(username) ??
                      null
                    : null
            if (!userId || !frame.openProfile) {
                throw new BridgeError('Only a user this ordinal was shown can be opened')
            }
            appAction(frame, () => frame.openProfile!(userId))
            return {}
        }
        case 'openPost':
            if (!frame.openPost || !frame.context().post?.threadId) {
                throw new BridgeError('This ordinal is not in a post')
            }
            appAction(frame, frame.openPost)
            return {}
        default:
            throw new BridgeError(
                `${invocation.call} is not supported`,
                WALLET_ERROR.unsupportedAction,
            )
    }
}

function showProfile(frame: OrdinalBridgeFrame, username: string, userId: string) {
    let shown = profilesShownTo.get(frame.origin)
    if (!shown) profilesShownTo.set(frame.origin, (shown = new Map()))
    shown.set(username, userId)
}

/** The Treechat names behind payment addresses: public profile fields only, so no Connect.
 * An address with no single Treechat account maps to null. */
async function getUsers(args: unknown, frame: OrdinalBridgeFrame) {
    requireSignedIn()
    const addresses = (args as { addresses?: unknown } | null)?.addresses
    if (
        !Array.isArray(addresses) ||
        addresses.length === 0 ||
        addresses.length > MAX_USERS_PER_CALL ||
        addresses.some(address => typeof address !== 'string' || !PAYMENT_ADDRESS.test(address))
    ) {
        throw new BridgeError(
            `addresses must list 1-${MAX_USERS_PER_CALL} payment addresses`,
            WALLET_ERROR.invalidParameter,
        )
    }
    const wanted = [...new Set(addresses as string[])]
    let asked = addressesAskedBy.get(frame.origin)
    if (!asked) addressesAskedBy.set(frame.origin, (asked = new Set()))
    const fresh = wanted.filter(address => !asked!.has(address))
    if (asked.size + fresh.length > MAX_USERS_PER_ORDINAL) {
        throw new BridgeError('This ordinal has looked up too many addresses')
    }
    fresh.forEach(address => asked!.add(address))

    const missing = wanted.filter(address => !usersByAddress.has(address))
    if (missing.length) {
        let found: Awaited<ReturnType<typeof api.getOrdinalWalletUsers>>
        try {
            found = await api.getOrdinalWalletUsers(missing)
        } catch (error) {
            // Not counted against the ordinal: it may ask again.
            fresh.forEach(address => asked!.delete(address))
            throw new BridgeError(error instanceof Error ? error.message : 'Lookup failed')
        }
        const byAddress = new Map(found.map(entry => [entry.address, entry.user]))
        for (const address of missing) {
            const user = byAddress.get(address)
            usersByAddress.set(
                address,
                user
                    ? {
                          id: String(user.id),
                          user: {
                              username: user.username || null,
                              avatarUrl: user.avatar_url || null,
                          },
                      }
                    : null,
            )
        }
    }

    const result: Record<string, OrdinalBridgeUser | null> = {}
    for (const address of wanted) {
        const entry = usersByAddress.get(address) ?? null
        result[address] = entry ? { ...entry.user } : null
        if (entry?.user.username) showProfile(frame, entry.user.username, entry.id)
    }
    return result
}

/** Runs an action that changes what the viewer sees, at most once every couple of seconds per
 * ordinal. A fullscreen ordinal covers the app, so it leaves fullscreen first. */
function appAction(frame: OrdinalBridgeFrame, run: () => void) {
    const now = Date.now()
    if (now - (lastAppActionAt.get(frame.origin) ?? -Infinity) < APP_ACTION_GAP_MS) {
        throw new BridgeError('Try again in a moment')
    }
    lastAppActionAt.set(frame.origin, now)
    if (typeof document !== 'undefined') {
        document
            .querySelectorAll<HTMLDialogElement>('dialog[open][data-ordinal-viewer]')
            .forEach(dialog => dialog.close())
    }
    run()
}

function requireSignedIn() {
    if (!window.gon?.currentUser?.id) throw new BridgeError('Sign in to Treechat first')
}

const IDENTITY_KEY_PATTERN = /^0[23][0-9a-f]{64}$/
let fetchedIdentityKey: Promise<string | null> | null = null

// Older accounts carry the root in their session options; newer ones (browser-wallet
// signups) keep it in its own column, so ask the server once.
async function identityKey(): Promise<string | null> {
    const key = (window.gon?.currentUser?.options as any)?.bsv_wallet_root_identity?.public_key
    if (typeof key === 'string' && IDENTITY_KEY_PATTERN.test(key)) return key
    fetchedIdentityKey ||= fetch(`${window.knovApiUrl}/api/v1/bsv/root_identity`, {
        headers: { ...accessTokens(), Accept: 'application/json' },
    })
        .then(response => (response.ok ? response.json() : null))
        .then(body => {
            const fetched = body?.root_identity_key
            return typeof fetched === 'string' && IDENTITY_KEY_PATTERN.test(fetched)
                ? fetched
                : null
        })
        .catch(() => null)
    const fetched = await fetchedIdentityKey
    // A failed lookup may succeed later (e.g. once the wallet is set up).
    if (!fetched) fetchedIdentityKey = null
    return fetched
}

async function getPublicKey(args: unknown, frame: OrdinalBridgeFrame) {
    // Derived (BRC-42) keys come with signing in a later version.
    if ((args as { identityKey?: unknown } | null)?.identityKey !== true) {
        throw new BridgeError('Only the identity key is available', WALLET_ERROR.unsupportedAction)
    }
    await requireConnection(frame)
    const publicKey = await identityKey()
    if (!publicKey) throw new BridgeError('This wallet has no identity key yet')
    return { publicKey }
}

// One tray at a time: a second prompt would replace the first. A bridge prompt never
// replaces the app's own tray, and settles as declined if anything replaces it.
function prompt(
    message: React.ReactNode,
    confirmLabel: string,
    options: Record<string, unknown>,
): Promise<boolean> {
    const next = promptQueue.then(
        () =>
            new Promise<boolean>((resolve, reject) => {
                if (useStore.getState().isTrayOpen) {
                    reject(new BridgeError('Treechat is busy; try again in a moment'))
                    return
                }
                // A fullscreen ordinal sits in the top layer, above the app's tray: show the
                // prompt inside its viewer instead, so the ordinal stays fullscreen behind it.
                // Escape (the dialog's close request) declines the prompt and stays fullscreen.
                const viewer =
                    typeof document !== 'undefined'
                        ? document.querySelector<HTMLDialogElement>(
                              'dialog[open][data-ordinal-viewer]',
                          )
                        : null
                let opened: unknown = null
                const keepFullscreen = (event: Event) => {
                    event.preventDefault()
                    if (useStore.getState().trayParams === opened) {
                        useStore.getState().actions.trayActions.closeTray()
                    }
                }
                viewer?.addEventListener('cancel', keepFullscreen)
                let settled = false
                let unsubscribe = () => {}
                const settle = (value: boolean) => {
                    if (settled) return
                    settled = true
                    unsubscribe()
                    viewer?.removeEventListener('cancel', keepFullscreen)
                    resolve(value)
                }
                useStore
                    .getState()
                    .actions.trayActions.openConfirmTray(
                        message,
                        confirmLabel,
                        () => settle(true),
                        {
                            primaryActionFirst: true,
                            cancelMessage: 'Cancel',
                            ...options,
                            container: viewer ?? undefined,
                            // Settle before the tray closes, so closing reads as the answer given.
                            awaitConfirm: true,
                            disableEnterConfirm: true,
                            confirmDelayMs: 600,
                            onCancel: () => settle(false),
                        },
                    )
                opened = useStore.getState().trayParams
                unsubscribe = useStore.subscribe(state => {
                    if (state.trayParams !== opened) settle(false)
                })
            }),
    )
    promptQueue = next.catch(() => undefined)
    return next
}

// Identity is shared once per ordinal per page visit; a refusal sticks so an ordinal
// can't re-prompt in a loop.
async function requireConnection(frame: OrdinalBridgeFrame) {
    requireSignedIn()
    const state = connections.get(frame.origin)
    if (state === 'granted') return
    if (state === 'denied') throw new BridgeError('The viewer declined to connect')

    let pending = pendingConnections.get(frame.origin)
    if (!pending) {
        pending = prompt(<ConnectMessage label={frame.label()} />, 'Connect', {
            title: 'Connect ordinal',
        }).then(
            granted => {
                pendingConnections.delete(frame.origin)
                // Stopped while the prompt was up: another ordinal holds the wallet now.
                const running = [...frames.values()].some(other => other.origin === frame.origin)
                if (granted && frames.size && !running) return false
                if (granted) {
                    // Connecting one ordinal disconnects the one before it.
                    for (const origin of [...connections.keys()]) {
                        if (connections.get(origin) === 'granted') connections.delete(origin)
                    }
                }
                connections.set(frame.origin, granted ? 'granted' : 'denied')
                return granted
            },
            error => {
                // The prompt never reached the viewer (another tray was open): let them retry.
                pendingConnections.delete(frame.origin)
                throw error
            },
        )
        pendingConnections.set(frame.origin, pending)
    }
    if (!(await pending)) throw new BridgeError('The viewer declined to connect')
}

/** Start an action without ever paying twice. A lost answer or a server error leaves it
 * unknown whether the payment started, so ask again with the same payment ID (the server
 * treats a repeat as the same payment), then ask whether it exists. Only a definite refusal,
 * or the server saying it never received the payment, ends it. If it still can't be told, the
 * ordinal gets no new payment until the page reloads. */
async function startAction(
    origin: string,
    params: Parameters<typeof api.createOrdinalWalletAction>[0],
): Promise<OrdinalWalletActionResult> {
    const uncertain = (res: OrdinalWalletActionResult) =>
        res.status === 'error' && (res.httpStatus === undefined || res.httpStatus >= 500)
    for (let attempt = 0; attempt < START_ATTEMPTS; attempt++) {
        if (attempt) await sleep(START_RETRY_MS * attempt)
        const res = await api.createOrdinalWalletAction(params)
        if (!uncertain(res)) return res
    }
    for (let attempt = 0; attempt < START_ATTEMPTS; attempt++) {
        const res = await api.getOrdinalWalletAction(params.paymentId)
        if (res.status !== 'error')
            return { status: 'temporal_pending', payment_id: params.paymentId }
        if (res.httpStatus === 404)
            throw new BridgeError('Treechat could not be reached. Nothing was paid.')
        await sleep(START_RETRY_MS)
    }
    unsettledOrigins.add(origin)
    throw new BridgeError(
        'Treechat could not confirm whether this payment started. Reload the page before trying again.',
    )
}

type StartParams = Parameters<typeof api.createOrdinalWalletAction>[0]

interface PreparedAction {
    paymentId: string
    startedAt: number
    /** Settles once the server has (or hasn't) started the action. */
    started: Promise<void>
    /** The funded, unsigned transaction for the ordinal to unlock. */
    signable: Promise<string>
    discarded: boolean
}

/** Start a deferred action and wait for its funded transaction. Runs while the viewer reads
 * the confirm tray, so approving only releases what is already funded. */
function prepareDeferredAction(origin: string, params: StartParams): PreparedAction {
    const prepared: PreparedAction = {
        paymentId: params.paymentId,
        startedAt: Date.now(),
        started: Promise.resolve(),
        signable: Promise.resolve(''),
        discarded: false,
    }
    prepared.started = startAction(origin, params).then(started => {
        if (
            started.status === 'error' ||
            started.status === 'conflict' ||
            started.status === 'failed'
        ) {
            throw new BridgeError(started.error || 'Payment failed')
        }
    })
    prepared.signable = prepared.started.then(async () => {
        try {
            return await waitForSignable(params.paymentId, () => prepared.discarded)
        } catch (error) {
            if (!prepared.discarded) {
                prepared.discarded = true
                await api.abortOrdinalWalletAction(params.paymentId).catch(() => undefined)
            }
            throw error
        }
    })
    // Nobody may be listening yet (the tray is still open).
    prepared.signable.catch(() => undefined)
    return prepared
}

/** Give back what a prepared action holds: the viewer declined, or it got too old. With
 * awaitRelease, resolves true only once the server says the action has ended, its coins
 * released; otherwise once the abort is sent. */
async function discardPreparedAction(
    prepared: PreparedAction,
    declined: boolean,
    awaitRelease = false,
) {
    prepared.discarded = true
    try {
        await prepared.started
    } catch {
        return true // never started: nothing to give back
    }
    for (let attempt = 0; attempt < DISCARD_ATTEMPTS; attempt++) {
        if (attempt) await sleep(START_RETRY_MS)
        const res = await api.abortOrdinalWalletAction(prepared.paymentId, { declined })
        if (res.status !== 'error') break
        // Only no answer or a server error is worth asking again. A refusal may mean the
        // action already ended (the server gave up on it), which the checks below can tell.
        if (res.httpStatus !== undefined && res.httpStatus < 500) {
            if (!awaitRelease) return false
            break
        }
    }
    if (!awaitRelease) return true
    // The abort only signals the workflow; it releases the coins, then reads as ended.
    for (let attempt = 0; attempt < RELEASE_CHECKS; attempt++) {
        await sleep(POLL_INTERVAL_MS)
        const res = await api.getOrdinalWalletAction(prepared.paymentId)
        if ((res.status === 'failed' && res.released) || res.httpStatus === 404) return true
    }
    return false
}

async function createAction(args: unknown, frame: OrdinalBridgeFrame) {
    const action = validateCreateActionArgs(args)
    requireSignedIn()
    if (unsettledOrigins.has(frame.origin)) {
        throw new BridgeError(
            'An earlier payment from this ordinal is still unconfirmed. Reload the page before trying again.',
        )
    }
    if (blockedPayments.has(frame.origin)) {
        throw new BridgeError('The viewer blocked payments from this ordinal')
    }
    if (Date.now() - (declinedAt.get(frame.origin) ?? -Infinity) < DECLINE_COOLDOWN_MS) {
        throw new BridgeError(
            'The viewer just declined a payment from this ordinal; try again in a moment',
        )
    }
    pruneDeferredActions()
    if (
        pendingActions.has(frame.origin) ||
        [...deferredActions.values()].some(entry => entry.origin === frame.origin)
    ) {
        throw new BridgeError('Another payment from this ordinal is waiting')
    }
    pendingActions.add(frame.origin)
    // Funded ahead of the viewer's answer; given back unless the ordinal gets it.
    let prepared: PreparedAction | null = null
    let confirmed = false
    let handedOver = false
    try {
        const [rate, address] = await Promise.all([
            api
                .getBsvExchangeRate()
                .then((data: { exchangerate?: number } | null) => data?.exchangerate ?? null)
                .catch(() => null),
            // To tell which outputs come back to the viewer. A cold cache gets a few seconds to
            // fill; past that the outputs stay unmarked and count as sent elsewhere.
            viewerPaymentAddress(PROMPT_ADDRESS_ATTEMPTS),
        ])
        const origin = frame.origin
        let deviceId: string | undefined
        if (currentUserUsesClientSigning()) {
            deviceId = (await getClientShare(window.gon.currentUser!.id))?.deviceId
            if (!deviceId) throw new BridgeError('Set up your wallet on this device first')
        }
        const startParams = (): StartParams => ({
            origin: frame.origin,
            paymentId: crypto.randomUUID(),
            outputs: action.outputs,
            deviceId,
            inputs: action.inputs,
            inputBeef: action.inputBEEF,
        })
        // The ordinal unlocks its own inputs only after approval, so funding one can start now.
        prepared = action.inputs ? prepareDeferredAction(origin, startParams()) : null
        const inputSats = action.inputs ? inputSatoshis(action.inputs, action.inputBEEF) : null
        const ownOutputs = outputsToAddress(action.outputs, address)
        const summary = actionSummary(action.outputs, inputSats, ownOutputs)
        // The button says what the viewer's wallet does, never what the ordinal calls it.
        const paying = summary.walletPays > 0
        confirmed = await prompt(
            <ActionMessage
                label={frame.label()}
                action={action}
                exchangeRate={rate}
                inputSats={inputSats}
                ownOutputs={ownOutputs}
                summary={summary}
                onBlock={() => {
                    blockedPayments.add(origin)
                    useStore.getState().actions.trayActions.closeTray()
                }}
            />,
            paying ? `Pay ${summary.walletPays.toLocaleString()} sats` : 'Approve',
            {
                title: paying ? 'Confirm payment' : 'Approve transaction',
                confirmVariant: 'bsv',
            },
        )
        if (!confirmed) {
            if (blockedPayments.has(origin)) {
                throw new BridgeError('The viewer blocked payments from this ordinal')
            }
            declinedAt.set(origin, Date.now())
            throw new BridgeError('The viewer declined the payment')
        }

        if (!prepared) {
            const params = startParams()
            const started = await startAction(frame.origin, params)
            if (
                started.status === 'error' ||
                started.status === 'conflict' ||
                started.status === 'failed'
            ) {
                throw new BridgeError(started.error || 'Payment failed')
            }
            return await waitForAction(params.paymentId, action.returnTXIDOnly, frame.origin)
        }

        // Deferred: hand the ordinal the funded, unsigned transaction to unlock its inputs.
        if (Date.now() - prepared.startedAt > PREPARED_MAX_AGE_MS) {
            // Its coins fund the new one, so they must be free first.
            if (!(await discardPreparedAction(prepared, true, true))) {
                throw new BridgeError('Treechat could not prepare this payment; try again')
            }
            prepared = prepareDeferredAction(origin, startParams())
        }
        const { paymentId } = prepared
        const signableBeef = await prepared.signable
        const reference = btoa(paymentId)
        deferredActions.set(reference, {
            paymentId,
            expiresAt: Date.now() + DEFERRED_ACTION_TTL_MS,
            origin: frame.origin,
            returnTXIDOnly: action.returnTXIDOnly,
        })
        handedOver = true
        return { signableTransaction: { tx: hexToBytes(signableBeef), reference } }
    } finally {
        pendingActions.delete(frame.origin)
        if (prepared && !handedOver && !prepared.discarded) {
            void discardPreparedAction(prepared, !confirmed)
        }
    }
}

// signAction: no new prompt; the viewer approved this exact action at createAction.
async function signAction(args: unknown, frame: OrdinalBridgeFrame) {
    const { reference, spends } = validateSignActionArgs(args)
    pruneDeferredActions()
    const entry = deferredActions.get(reference)
    if (!entry || entry.origin !== frame.origin) {
        throw new BridgeError(
            'No action from this ordinal matches that reference',
            WALLET_ERROR.invalidParameter,
        )
    }
    const submitted = await api.signOrdinalWalletAction(entry.paymentId, spends)
    // Whatever happens next, this reference is spent.
    deferredActions.delete(reference)
    // A definite refusal gives the coins back. Without an answer the unlock may still have
    // arrived, so wait for the real outcome rather than abort and invite a second payment.
    const refused = submitted.httpStatus !== undefined && submitted.httpStatus < 500
    if (submitted.status === 'error' && refused) {
        if (submitted.httpStatus !== 409) {
            await api.abortOrdinalWalletAction(entry.paymentId).catch(() => undefined)
        }
        throw new BridgeError(submitted.error || 'Signing failed')
    }
    return await waitForAction(entry.paymentId, entry.returnTXIDOnly, entry.origin)
}

async function abortAction(args: unknown, frame: OrdinalBridgeFrame) {
    const { reference } = validateAbortActionArgs(args)
    pruneDeferredActions()
    const entry = deferredActions.get(reference)
    if (!entry || entry.origin !== frame.origin) {
        throw new BridgeError(
            'No action from this ordinal matches that reference',
            WALLET_ERROR.invalidParameter,
        )
    }
    const res = await api.abortOrdinalWalletAction(entry.paymentId)
    if (res.status === 'error') {
        // No answer, or a server error: the action may still hold its coins, so keep the
        // reference and let the ordinal try again.
        if (res.httpStatus === undefined || res.httpStatus >= 500) {
            throw new BridgeError('Treechat could not cancel the action yet. Try again.')
        }
        deferredActions.delete(reference)
        throw new BridgeError(res.error || 'The action could not be cancelled')
    }
    deferredActions.delete(reference)
    return { aborted: true }
}

const sleep = (ms: number) => new Promise(resolve => window.setTimeout(resolve, ms))

const ADDRESS_ATTEMPTS = 10
const PROMPT_ADDRESS_ATTEMPTS = 3
const ADDRESS_POLL_MS = 1500

/** The address the viewer's balance watches, so an ordinal can pay them back. The endpoint
 * only reads a short-lived cache: the first request queues the job that fills it on a miss,
 * and later reads skip the queue and wait for that job, as the wallet panel does. */
async function viewerPaymentAddress(attempts = ADDRESS_ATTEMPTS): Promise<string | null> {
    for (let attempt = 0; attempt < attempts; attempt++) {
        if (attempt > 0) await sleep(ADDRESS_POLL_MS)
        const address = await api
            .getBsvAddress(attempt === 0 ? undefined : { enqueue: false })
            .then((data: { address?: unknown } | null) => data?.address)
            .catch(() => null)
        if (typeof address === 'string' && address) return address
    }
    return null
}

/** stopped: the action was given back, so stop waiting for it. */
async function waitForSignable(paymentId: string, stopped = () => false): Promise<string> {
    const deadline = Date.now() + UNLOCK_READY_TIMEOUT_MS
    while (Date.now() < deadline) {
        await sleep(POLL_INTERVAL_MS)
        if (stopped()) throw new BridgeError('The payment was cancelled')
        const res = await api.getOrdinalWalletAction(paymentId)
        if (res.status === 'awaiting_unlock' && res.signable_beef) return res.signable_beef
        if (res.status === 'failed') {
            const message = res.error || 'Payment failed'
            const code = /insufficient|not enough/i.test(message)
                ? WALLET_ERROR.insufficientFunds
                : WALLET_ERROR.unknown
            throw new BridgeError(message, code)
        }
    }
    throw new BridgeError(`Payment ${paymentId} could not be prepared in time`)
}

/** Sleeps that end early when the server pushes news of this payment (its status or txid
 * changed). One that arrives between sleeps ends the next sleep at once. */
function paymentWaker(paymentId: string) {
    let updated = false
    let wake: (() => void) | null = null
    const unsubscribe = subscribePaymentUpdate(paymentId, () => {
        updated = true
        wake?.()
    })
    return {
        sleep: (ms: number) =>
            new Promise<void>(resolve => {
                if (updated) {
                    updated = false
                    return resolve()
                }
                const done = () => {
                    if (wake === done) wake = null
                    updated = false
                    resolve()
                }
                wake = done
                window.setTimeout(done, ms)
            }),
        stop: unsubscribe,
    }
}

async function waitForAction(paymentId: string, returnTXIDOnly: boolean, origin: string) {
    const waker = paymentWaker(paymentId)
    try {
        return await pollAction(paymentId, returnTXIDOnly, origin, waker.sleep)
    } finally {
        waker.stop()
    }
}

async function pollAction(
    paymentId: string,
    returnTXIDOnly: boolean,
    origin: string,
    pause: (ms: number) => Promise<void>,
) {
    const startedAt = Date.now()
    const deadline = startedAt + ACTION_TIMEOUT_MS
    let beefDeadline: number | null = null
    while (Date.now() < deadline) {
        await pause(
            Date.now() - startedAt < 2 * 60 * 1000 ? POLL_INTERVAL_MS : SLOW_POLL_INTERVAL_MS,
        )
        const res = await api.getOrdinalWalletAction(paymentId)
        if (res.status === 'unknown') {
            // It may have been paid: no new payment from this ordinal until reload.
            unsettledOrigins.add(origin)
            throw new BridgeError(
                res.error || "This payment's outcome is unknown; check your wallet",
            )
        }
        if (res.status === 'failed') {
            const message = res.error || 'Payment failed'
            const code = /insufficient|not enough/i.test(message)
                ? WALLET_ERROR.insufficientFunds
                : WALLET_ERROR.unknown
            throw new BridgeError(message, code)
        }
        if (res.status !== 'completed' || !res.tx_id) continue

        if (res.beef) return { txid: res.tx_id, tx: hexToBytes(res.beef) }
        if (returnTXIDOnly) return { txid: res.tx_id }
        beefDeadline ??= Date.now() + BEEF_GRACE_MS
        if (Date.now() > beefDeadline) {
            // The money is already sent: a retry would pay again, so block new payments.
            unsettledOrigins.add(origin)
            throw new BridgeError(
                `Payment sent in ${res.tx_id}, but its transaction data isn't available yet`,
            )
        }
    }
    // It may still complete, so this ordinal gets no new payment until the page reloads.
    unsettledOrigins.add(origin)
    throw new BridgeError(`Payment ${paymentId} is still pending and may complete; don't retry it`)
}

function outputLabel(output: ActionOutput): string {
    const described = describeLockingScript(output.lockingScript)
    if (described.kind === 'address') {
        const address = Utils.toBase58Check(hexToBytes(described.hash!), [0])
        return `${address.slice(0, 8)}…${address.slice(-4)}`
    }
    return described.kind === 'data' ? 'Data (OP_RETURN)' : 'Custom script'
}

// Long payouts (a contract paying many people) show their first few outputs and a total.
const VISIBLE_OUTPUTS = 6
function visibleOutputs(outputs: ActionOutput[]): ActionOutput[] {
    return outputs.length <= VISIBLE_OUTPUTS ? outputs : outputs.slice(0, VISIBLE_OUTPUTS - 1)
}

const secondary: React.CSSProperties = { fontSize: '12px', color: 'var(--color-text-secondary)' }

function ConnectMessage({ label }: { label: string }) {
    return (
        <div style={{ textAlign: 'left' }}>
            <div style={{ fontSize: '15px', fontWeight: 600 }}>{label}</div>
            <div style={{ ...secondary, marginTop: '8px' }}>
                Lets this ordinal see your username, avatar and wallet identity key. Every payment
                still asks you first.
            </div>
        </div>
    )
}

function AmountRow({ label, sats, note }: { label: string; sats: number; note?: string }) {
    return (
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px' }}>
            <span>{label}</span>
            <span style={{ flexShrink: 0 }}>
                {sats.toLocaleString()} sats{note ? ` · ${note}` : ''}
            </span>
        </div>
    )
}

function ActionMessage({
    label,
    action,
    exchangeRate,
    inputSats,
    ownOutputs,
    summary,
    onBlock,
}: {
    label: string
    action: ValidatedCreateAction
    exchangeRate: number | null
    // What the ordinal's own inputs bring, or null when there are none or their value is unknown.
    inputSats: number | null
    // Outputs that pay the viewer's own address.
    ownOutputs: Set<number>
    summary: ActionSummary
    onBlock: () => void
}) {
    const visible = visibleOutputs(action.outputs)
    const usd = satsToUsd(summary.walletPays, exchangeRate)
    const inputsUnknown = !!action.inputs && inputSats === null
    return (
        <div style={{ textAlign: 'left' }}>
            <div style={{ ...secondary, fontWeight: 700 }}>{label.toUpperCase()}</div>
            <div style={{ marginTop: '6px', fontSize: '15px', overflowWrap: 'anywhere' }}>
                {action.description}
            </div>
            <div
                style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '12px' }}
            >
                {summary.receive > 0 && <AmountRow label="You receive" sats={summary.receive} />}
                <div style={{ fontWeight: 700 }}>
                    <AmountRow
                        label="Your wallet pays"
                        sats={summary.walletPays}
                        note={usd !== null ? formatUsdValue(usd) : undefined}
                    />
                </div>
                <AmountRow label="Sent elsewhere" sats={summary.elsewhere} />
            </div>
            <div style={{ ...secondary, marginTop: '8px' }}>Plus a small network fee.</div>
            {/* Anything leaving for someone else is shown, not tucked away. */}
            <details
                open={summary.elsewhere > 0 || inputsUnknown}
                style={{
                    marginTop: '12px',
                    paddingTop: '10px',
                    borderTop: '1px solid var(--color-border-default)',
                }}
            >
                <summary style={{ ...secondary, cursor: 'pointer' }}>Show details</summary>
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '8px',
                        marginTop: '10px',
                    }}
                >
                    {visible.map((output, index) => (
                        <div
                            key={index}
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                gap: '16px',
                            }}
                        >
                            <div style={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                                <div>
                                    {outputLabel(output)}
                                    {ownOutputs.has(index) ? ' (you)' : ''}
                                </div>
                                {output.outputDescription && (
                                    <div style={secondary}>{output.outputDescription}</div>
                                )}
                            </div>
                            <div style={{ flexShrink: 0 }}>
                                {output.satoshis.toLocaleString()} sats
                            </div>
                        </div>
                    ))}
                    {action.outputs.length > visible.length && (
                        <div style={secondary}>
                            and {action.outputs.length - visible.length} more outputs totalling{' '}
                            {action.outputs
                                .slice(visible.length)
                                .reduce((sum, output) => sum + output.satoshis, 0)
                                .toLocaleString()}{' '}
                            sats
                        </div>
                    )}
                    {inputSats !== null && (
                        <div style={secondary}>
                            <AmountRow label="From the ordinal" sats={inputSats} />
                        </div>
                    )}
                    {inputsUnknown && (
                        <div style={secondary}>
                            Also spends{' '}
                            {action.inputs!.length === 1
                                ? 'an output'
                                : `${action.inputs!.length} outputs`}{' '}
                            the ordinal supplies, whose value Treechat couldn&apos;t read.
                        </div>
                    )}
                </div>
            </details>
            <button
                type="button"
                onClick={onBlock}
                style={{
                    ...secondary,
                    marginTop: '10px',
                    padding: 0,
                    border: 0,
                    background: 'none',
                    textDecoration: 'underline',
                    cursor: 'pointer',
                }}
            >
                Block this ordinal
            </button>
        </div>
    )
}
